import { styled } from '@mui/material/styles';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import CustomText from 'components/common/CustomText/CustomText';
import { Avatar, Box, IconButton, Stack } from '@mui/material';
import { colors } from 'styles/colors';
import { getIsDoctor, hexWithOpacity, toTitleCase } from 'common/utils';
import DashboardIcon from 'icons/DashboardIcon';
import { useGetDoctors } from 'hooks/useUsers';
import { useAuth } from 'context/AuthContext';
import { EUsersFilter } from 'enum/EUsersFilter';
import useGlobalStyles from 'styles/globalStyles';

interface Props {
  onClose: () => void;
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  padding: 0,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<Box sx={{
      width: 25,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <DashboardIcon size={14} color={colors.black} />
    </Box>}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  padding: '0 0 0 8px',
  borderRadius: '10px',

  '&:hover': {
    backgroundColor: hexWithOpacity(colors.black, 0.04),
  },

  /* kill ALL animations and transitions */
  '& .MuiAccordionSummary-expandIconWrapper': {
    transition: 'none',
  },

  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'none',
  },


  '& .MuiAccordionSummary-content': {
    marginLeft: '5px',
  },

  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
}));



export default function DashboardsAccordion({ onClose }: Props) {
  const { user, setScope, doctor } = useAuth();
  const globalStyles = useGlobalStyles();

  const { data: dashboard, isLoading, isFetching } = useGetDoctors({
    userId: user?.id,
    filter: EUsersFilter.MY_TEAMS,
  }, {
    enabled: !!user,
    onSuccess: (data: any) => { },
    onError: (error: any) => { }
  });


  const handleDashboardClick = (doctor: any) => {
    setScope(doctor);
    onClose();

  }

  return (
    <div>
      <Accordion defaultExpanded={false} elevation={0}>
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
        >
          <CustomText text="Other Dashboards" color={colors.black} />
        </AccordionSummary>

        <AccordionDetails>

          {dashboard?.doctors?.length == 0 && (
            <Box py={1}>
              <CustomText
                text='No other dashbaords'
                textAlign={'center'}
                fontSize={14}
                color={colors.secondary}
              />
            </Box>
          )}

          {dashboard?.doctors?.length > 0 && <Box>
            <IconButton
              onClick={() => handleDashboardClick(null)}
              className={globalStyles.popoverButtonContainer}
              sx={{ pl: 2 }}
            >
              <Box className={globalStyles.popoverIconContainer}>
                <Avatar
                  src={user?.profile_image?.href_small}
                  alt={'profile'}
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: colors.avatarBg,
                  }}
                />
              </Box>
              <CustomText
                text={`My dashboard`}
                ml="5px"
                color={colors.black}
                textAlign={'start'}
                fontSize={13}
                fontWeight={doctor ? 500 : 600}
              />
            </IconButton>
          </Box>}

          {dashboard?.doctors?.map((item: any) => {

            const isDoctor = getIsDoctor(item.user_role?.name)

            return (
              <IconButton
                onClick={() => handleDashboardClick(item)}
                className={globalStyles.popoverButtonContainer}
                sx={{ pl: 2 }}
                key={item.id}
              >
                <Stack>
                  <Stack direction='row' alignItems='center'>
                    <Box className={globalStyles.popoverIconContainer}>
                      <Avatar
                        src={item?.user_media?.image?.href_small}
                        alt={'profile'}
                        sx={{
                          width: 18,
                          height: 18,
                          bgcolor: colors.avatarBg,
                        }}
                      />
                    </Box>
                    <Box ml={'5px'}>
                      <CustomText
                        text={`${isDoctor ? 'Dr.' : ''} ${item.name}'s dashboard`}
                        color={colors.black}
                        textAlign={'start'}
                        fontSize={13}
                        fontWeight={doctor?.id == item.id ? 600 : 500}
                      />
                      <CustomText
                        text={toTitleCase(item.permission)}
                        textAlign={'start'}
                        fontSize={10}
                        color={colors.secondary}
                        lineHeight={0.8}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </IconButton>
            )
          })}

        </AccordionDetails>
      </Accordion>
    </div>
  );
}
